import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { api, SESSION_EXPIRED_EVENT } from '../api/client';
import { useFieldRole } from '../hooks/useFieldRole';
import { hasAuditRole } from '../lib/jwtRoles';
import { useTheme } from '../hooks/useTheme';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { Button, ResponsiveDialog, Select } from './ui';
import { allNavItems, type NavRoles } from './shell/navItems';
import { NavIcon } from './shell/NavIcon';
import { TabBar } from './shell/TabBar';

interface RealmOption {
  realm: string;
  displayName: string;
}

export function hasAdminRole(token?: string): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Check realm_access roles
    if (payload.realm_access?.roles?.includes('billing:admin')) return true;
    // Check resource_access roles
    for (const client of Object.values(payload.resource_access || {} as Record<string, { roles?: string[] }>)) {
      if ((client as { roles?: string[] }).roles?.includes('billing:admin')) return true;
    }
    return false;
  } catch { return false; }
}

export default function AppShell() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const loginRealm = localStorage.getItem('billing_realm') || '';
  const isAdmin = hasAdminRole(auth.user?.access_token);
  const fieldRoles = useFieldRole();
  // Audit Logs needs billing:audit (Beau and Jean), not billing:admin; see
  // docs/superpowers/specs/2026-10-01-audit-log-access-search-export-design.md.
  // The API enforces the same rule (403) and also requires a master-issued token.
  const canAudit = hasAuditRole(auth.user?.access_token);
  const roles: NavRoles = { isAdmin, hasFieldRole: fieldRoles.hasAny, canAudit };
  const navItems = allNavItems(roles);

  // For admin users, track the active realm separately from the login realm
  const [activeRealm, setActiveRealm] = useState(() => localStorage.getItem('billing_active_realm') || loginRealm);
  const [realms, setRealms] = useState<RealmOption[]>([]);
  const [realmKey, setRealmKey] = useState(0); // Force re-mount of Outlet on realm change

  const realm = isAdmin ? activeRealm : loginRealm;

  // Listen for 401 events from API client
  useEffect(() => {
    const handler = () => setShowSessionExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, []);

  // The More sheet is a navigation surface: leaving it open over the page the
  // user just navigated to would hide the thing they asked for.
  useEffect(() => setMoreOpen(false), [location.pathname]);

  // If the viewport crosses into desktop while the sheet is open, the sidebar
  // rail takes over and already lists every destination — an orphaned
  // centred "Menu" dialog floating over it serves no one.
  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (isDesktop) setMoreOpen(false);
  }, [isDesktop]);

  // Set API auth synchronously before children render AND on changes
  if (auth.user?.access_token && realm) {
    api.setAuth(auth.user.access_token, realm);
  }

  // Load realms for admin users (once per session, with a retry affordance).
  //
  // This was `.catch(() => {})` against an endpoint that itself collapsed failures into
  // `{ data: [] }`. Between the two, the 403 from the CRIT-2 ingress deny (2026-07-20)
  // rendered as simply "no dropdown" with no error anywhere, and the realm switcher
  // silently vanished for every billing:admin for eight days. Never swallow it again.
  const [realmsLoaded, setRealmsLoaded] = useState(false);
  const [realmsError, setRealmsError] = useState(false);
  useEffect(() => {
    if (isAdmin && auth.user?.access_token && !realmsLoaded) {
      setRealmsLoaded(true);
      setRealmsError(false);
      api.getRealms().then(res => {
        setRealms(res.data.map(r => ({ realm: r.realm, displayName: r.displayName })));
        // An empty list for an admin is itself abnormal — flag it rather than let it
        // masquerade as "you legitimately have no realms to switch to".
        if (res.data.length === 0) setRealmsError(true);
      }).catch(() => setRealmsError(true));
    }
  }, [isAdmin, realmsLoaded]);

  // Clearing the loaded flag re-triggers the effect above.
  const retryLoadRealms = () => setRealmsLoaded(false);

  const handleRealmChange = (newRealm: string) => {
    setActiveRealm(newRealm);
    localStorage.setItem('billing_active_realm', newRealm);
    api.setAuth(auth.user!.access_token, newRealm);
    setRealmKey(k => k + 1); // Force re-mount all child pages
    navigate('/'); // Navigate to dashboard
  };

  const handleLogout = () => {
    auth.signoutRedirect();
  };

  const handleBackToOR = () => {
    window.location.href = `https://iot.utilsync.cloud/manager/?realm=${realm}`;
  };

  const handleRelogin = () => {
    setShowSessionExpired(false);
    auth.signinRedirect();
  };

  // Extract user info
  const userName = auth.user?.profile?.preferred_username || auth.user?.profile?.name || 'User';

  const themeToggleLabel = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
  // `tap-target` (not bare `min-h-tap`) so the label is vertically centred in
  // the 44px box instead of sitting at the top of it — these are full-width
  // rows, so the gap was visible. `flex` overrides tap-target's inline-flex
  // (block-level, to pair with w-full inside the space-y-2 stack) and
  // `justify-start` overrides its justify-center, keeping these left-aligned
  // like the nav rail above them. Both are plain utilities and outrank the
  // component-layer class — see tailwind.config.js.
  const sidebarActionClass =
    'w-full tap-target flex justify-start text-left px-3 py-2 text-sm rounded-lg transition-colors hover:bg-surface-sunken';

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        {/* Sidebar — the lg+ rail. Below lg the TabBar and More sheet take over. */}
        <aside className="hidden lg:flex w-64 bg-surface-raised text-content border-r border-border-token flex-col">
          {/* Logo */}
          <div className="p-4 border-b border-border-token">
            <h1 className="text-xl font-bold text-accent">UtilSync Billing</h1>
            {isAdmin && realms.length > 0 ? (
              <Select
                value={activeRealm}
                onChange={(e) => handleRealmChange(e.target.value)}
                className="mt-1 text-xs"
                aria-label="Active realm"
              >
                {realms.map(r => (
                  <option key={r.realm} value={r.realm}>{r.displayName}</option>
                ))}
              </Select>
            ) : isAdmin && realmsError ? (
              <div className="mt-1">
                {/* amber-700, not amber-600: #d97706 on white is 3.19:1 and
                    fails AA at text-xs — pre-branch this text sat on
                    bg-gray-900 as amber-400 (10.63:1); the branch moved the
                    sidebar to a light surface and kept the light-mode
                    shade, a regression. amber-700 (#b45309, the same shade
                    --warning already uses) is 5.02:1 on white. This is the
                    realm-switcher-unavailable warning — the message the
                    eight-day-outage comment above exists to keep visible. */}
                <p className="text-xs text-amber-700 dark:text-amber-400">Realm: {realm} — list unavailable</p>
                <button
                  type="button"
                  onClick={retryLoadRealms}
                  className="tap-target text-xs text-accent underline hover:opacity-80"
                >
                  Retry
                </button>
              </div>
            ) : (
              <p className="text-xs text-content-muted mt-1">Realm: {realm}</p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1" aria-label="Sections">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `tap-target flex justify-start gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-accent text-accent-fg'
                      : 'text-content-muted hover:bg-surface-sunken hover:text-content'
                  }`
                }
              >
                <NavIcon name={item.icon} className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border-token">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-accent-fg font-semibold">
                {userName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{userName}</p>
                <p className="text-xs text-content-muted truncate">{realm}</p>
              </div>
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={toggle}
                className={`${sidebarActionClass} text-content-muted hover:text-content`}
              >
                {themeToggleLabel}
              </button>
              <button
                type="button"
                onClick={handleBackToOR}
                className={`${sidebarActionClass} text-content-muted hover:text-content`}
              >
                Back to UtilSync Portal
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className={`${sidebarActionClass} text-red-600 dark:text-red-400`}
              >
                Sign out
              </button>
            </div>
          </div>
        </aside>

        {/* Main column — mobile top bar stacked above the scroll area, so the
            header can't end up beside <main> in the outer flex row. min-w-0
            stops a wide child (a table) from pushing the page sideways. */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* bg-surface-raised (not plain bg-surface): the same elevation
              token every other floating chrome surface in the app uses
              (the lg+ sidebar rail, Card, ResponsiveDialog content) — light
              is unchanged (both are #ffffff) but in dark this makes the
              header agree with the body it sits above (#292524 on #0c0a09,
              1.30:1) the same direction light already had (#ffffff on
              #faf9f7, 1.05:1), rather than the barely-separated bg-surface
              (#1c1917 on #0c0a09, 1.13:1) that was also the ONE floating
              bar in the app not using the "raised" token. */}
          <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-border-token bg-surface-raised px-4 py-3 pt-safe-t">
            <span className="font-bold text-content">UtilSync Billing</span>
            <span className="text-xs text-content-muted">{realm}</span>
          </header>

          {/* pb-24 keeps the fixed bottom tab bar off the last row of content. */}
          <main className="flex-1 overflow-auto pb-24 lg:pb-0">
            <Outlet key={realmKey} />
          </main>
        </div>
      </div>

      <TabBar roles={roles} onMoreClick={() => setMoreOpen(true)} />

      {/* More sheet — the full nav list plus the session actions the rail owns at lg+. */}
      <ResponsiveDialog
        open={moreOpen}
        onOpenChange={setMoreOpen}
        title="Menu"
        description="All sections, plus theme and session actions."
      >
        <nav className="flex flex-col" aria-label="All sections">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setMoreOpen(false)}
              className="tap-target flex justify-start gap-3 rounded-md px-3 text-content hover:bg-surface-sunken"
            >
              <NavIcon name={item.icon} className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        {isAdmin && realms.length > 0 ? (
          <div className="mt-4 border-t border-border-token pt-4">
            <Select
              value={activeRealm}
              onChange={(e) => {
                handleRealmChange(e.target.value);
                setMoreOpen(false);
              }}
              aria-label="Active realm"
            >
              {realms.map((r) => (
                <option key={r.realm} value={r.realm}>{r.displayName}</option>
              ))}
            </Select>
          </div>
        ) : isAdmin && realmsError ? (
          <div className="mt-4 border-t border-border-token pt-4">
            {/* Same amber-700 fix as the sidebar rail's copy of this warning — see the comment there. */}
            <p className="text-xs text-amber-700 dark:text-amber-400">Realm: {realm} — list unavailable</p>
            <button
              type="button"
              onClick={retryLoadRealms}
              className="tap-target text-xs text-accent underline hover:opacity-80"
            >
              Retry
            </button>
          </div>
        ) : null}
        <div className="mt-4 border-t border-border-token pt-4 flex flex-col gap-2">
          <Button variant="secondary" onClick={toggle}>{themeToggleLabel}</Button>
          <Button variant="secondary" onClick={handleBackToOR}>Back to UtilSync Portal</Button>
          <Button variant="danger" onClick={handleLogout}>Sign out</Button>
        </div>
      </ResponsiveDialog>

      {/* Session Expired Modal - Only shown when actually expired */}
      <ResponsiveDialog
        open={showSessionExpired}
        onOpenChange={setShowSessionExpired}
        title="Session Expired"
        description="Your session has expired. Please sign in again to continue."
        footer={<Button onClick={handleRelogin}>Sign In Again</Button>}
      >
        <div className="flex justify-center py-2">
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>
      </ResponsiveDialog>
    </div>
  );
}
